import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SrIcon } from "@/components/ui/SrIcon";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import { useMissingHours } from "@/hooks/use-missing-hours";
import { formatMissingDay } from "@/lib/missing-hours";
import { osloDateString } from "@/lib/oslo-time";

const STORAGE_PREFIX = "revehiet:missing-hours-seen";

/**
 * Har brukeren allerede sett popup-en i dag? Lagres per bruker, ikke globalt,
 * så en delt maskin ikke skjuler varselet for neste person som logger inn.
 *
 * All localStorage-tilgang er pakket inn: i privat nettleservindu, med
 * blokkerte site-data eller i en innebygd webview kaster både lesing og
 * skriving. Feiler den, viser vi heller popup-en en gang for mye enn å la
 * hele flaten krasje.
 */
function seenToday(userId: string, today: string): boolean {
  try {
    return localStorage.getItem(`${STORAGE_PREFIX}:${userId}`) === today;
  } catch {
    return false;
  }
}

function markSeen(userId: string, today: string): void {
  try {
    localStorage.setItem(`${STORAGE_PREFIX}:${userId}`, today);
  } catch {
    // Ingen lagring tilgjengelig — popup-en dukker opp igjen ved neste last.
  }
}

/**
 * VIB-2093: påminnelse i appen om dager uten timeføring.
 *
 * Erik 28.09: dette skal være en popup i Revehiet, ikke en e-post. Cron-en
 * (VIB-1296) fortsetter å lage bjellevarselet; dette er flaten som faktisk
 * blir sett av en som er logget inn.
 *
 * Vises én gang per bruker per dag. En påminnelse som spretter opp ved hver
 * navigering blir en refleks å klikke bort, og da fanger den ikke dagen som
 * faktisk mangler.
 */
export default function MissingHoursDialog() {
  const navigate = useNavigate();
  const { activeUser } = useImpersonation();
  const { data } = useMissingHours();
  const [open, setOpen] = useState(false);

  const userId = activeUser?.id ?? null;
  const missingDays = data?.missingDays ?? [];
  const today = osloDateString(new Date());

  useEffect(() => {
    if (!userId || missingDays.length === 0) return;
    if (seenToday(userId, today)) return;
    setOpen(true);
  }, [userId, missingDays.length, today]);

  const close = () => {
    if (userId) markSeen(userId, today);
    setOpen(false);
  };

  const goToTimeRegistration = () => {
    close();
    navigate("/mine-oppgaver");
  };

  if (missingDays.length === 0) return null;

  const single = missingDays.length === 1;

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? setOpen(true) : close())}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <SrIcon name="alert-circle" className="h-5 w-5 text-amber-600" />
            {single ? "Du mangler timer for en dag" : "Du mangler timer for flere dager"}
          </DialogTitle>
          <DialogDescription>
            Vi finner ingen registrerte timer for{" "}
            {single ? "denne dagen" : "disse dagene"}:
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-1.5 py-1">
          {missingDays.map((day) => (
            <li key={day} className="flex items-center gap-2 text-sm text-foreground">
              <span className="h-1.5 w-1.5 flex-shrink-0 rounded-full bg-amber-500" />
              {formatMissingDay(day)}
            </li>
          ))}
        </ul>

        <p className="text-xs text-muted-foreground">
          Var du på ferie, avspaserte eller var syk, registrer fraværet i stedet,
          så slutter vi å spørre.
        </p>

        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row">
          <Button variant="outline" onClick={close} className="w-full sm:w-auto">
            Ikke nå
          </Button>
          <Button onClick={goToTimeRegistration} className="w-full sm:w-auto">
            Før timer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
